/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/draftorder
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyDraftOrderKey,
  ShopifyDraftOrderLineItemKey,
  ShopifyDraftOrderLineItemToDraftOrderKey,
  ShopifyDraftOrderLineItemToFulfillmentServiceKey,
  ShopifyDraftOrderLineItemToProductKey,
  ShopifyDraftOrderLineItemToProductVariantKey,
  ShopifyDraftOrderLineItemToShopKey,
  ShopifyFulfillmentServiceKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const fulfillmentServiceStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-DraftOrderLineItem-fulfillmentService-initial";
const totalDiscountSetKey = "ModelField-DataModel-Shopify-DraftOrderLineItem-totalDiscountSet";
const totalDiscountSetStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-DraftOrderLineItem-totalDiscountSet-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyDraftOrderLineItemKey,
  "2022-01": {
    name: "Shopify Draft Order Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Draft Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToDraftOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order_id" } },
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_id" } },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductVariantKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_id" } },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name" } },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_title" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "vendor" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gift_card" } },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_service" } },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "properties" } },
      },
      {
        name: "Applied Discount",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "applied_discount" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxable" } },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines" } },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price" } },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "grams" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "draftOrder" },
  },
  "2023-10": {
    fields: [
      {
        name: "Custom",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "custom" } },
      },
    ],
  },
  "2024-07": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Draft Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToDraftOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "order_id", graphQLFieldKey: "draftOrderId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
        },
      },
      {
        name: "Applied Discount",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "applied_discount",
            graphQLFieldKey: "appliedDiscount",
            graphQLSelection: `
          amountSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          description
          title
          value
          valueType
            `,
          },
        },
      },
      {
        name: "Approximate Discounted Unit Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "approximateDiscountedUnitPriceSet",
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "Bundle Components",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "bundleComponents",
            graphQLSelection: `
            id`,
          },
        },
      },
      {
        name: "Custom",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "custom", graphQLFieldKey: "custom" } },
      },
      {
        name: "Discounted Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "discountedTotalSet",
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "Fulfillment Service Handle",
        fieldType: FieldType.String,
        shouldConvergeField: (enabledModelKeys) => enabledModelKeys.some((key) => [ShopifyFulfillmentServiceKey].includes(key)),
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentServiceHandle",
            skipGraphQLSelection: true,
          },
        },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToFulfillmentServiceKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentService",
            graphQLSelection: `
            id
            handle`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentServiceKey,
        },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "grams" },
        },
        isDeleted: true,
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gift_card", graphQLFieldKey: "isGiftCard" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name", graphQLFieldKey: "name" } },
      },
      {
        name: "Original Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "originalTotalSet",
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "Original Unit Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "originalUnitPriceSet",
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "Original Unit Price With Currency",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "originalUnitPriceWithCurrency",
            graphQLSelection: `
            amount
            currencyCode`,
          },
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "product_id", graphQLFieldKey: "product", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "price", graphQLFieldKey: "price", skipGraphQLSelection: true },
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "tax_lines",
            graphQLFieldKey: "taxLines",
            graphQLSelection: `
            channelLiable
            priceSet {
            presentmentMoney{
                amount
                currencyCode
              }
              shopMoney {
                amount
                currencyCode
              }
            }
            rate
            ratePercentage
            title`,
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title", graphQLFieldKey: "title" } },
      },
      {
        name: "Total Discount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalDiscountSet",
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "UUID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "uuid" } },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "variant_id", graphQLFieldKey: "variant", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_title", graphQLFieldKey: "variantTitle" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "vendor", graphQLFieldKey: "vendor" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity", graphQLFieldKey: "quantity" } },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "properties", graphQLFieldKey: "properties", skipGraphQLSelection: true },
        },
      },
      {
        name: "Custom Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customAttributes",
            graphQLSelection: `
            key
            value`,
          },
        },
      },
      {
        name: "Custom Attributes V2",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customAttributesV2",
            graphQLSelection: `
            key
            value`,
          },
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping", graphQLFieldKey: "requiresShipping" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku", graphQLFieldKey: "sku" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxable", graphQLFieldKey: "taxable" } },
      },
      {
        name: "Weight",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "weight",
            graphQLSelection: `
            unit
            value`,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "draftOrders",
      resource: "draftOrder.lineItems",
    },
  },
  "2025-04": {
    fields: [
      {
        name: "Fulfillment Service",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToFulfillmentServiceKey,
        initialStorageEpochKey: fulfillmentServiceStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentService",
            shopifyFieldKey: "fulfillment_service",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
            graphQLSelection: `
            id
            handle`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentServiceKey,
        },
      },
      {
        name: "Total Discount Set",
        key: totalDiscountSetKey,
        initialStorageEpochKey: totalDiscountSetStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalDiscountSet",
            shopifyFieldKey: "total_discount_set",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
            graphQLSelection: `
            presentmentMoney{
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }`,
          },
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "requires_shipping",
            graphQLFieldKey: "requiresShipping",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "sku",
            graphQLFieldKey: "sku",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "taxable",
            graphQLFieldKey: "taxable",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_id",
            graphQLFieldKey: "variant",
            graphQLSelection: "id",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_title",
            graphQLFieldKey: "variantTitle",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "vendor",
            graphQLFieldKey: "vendor",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "quantity",
            graphQLFieldKey: "quantity",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "properties",
            graphQLFieldKey: "properties",
            skipGraphQLSelection: true,
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderLineItemToProductKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "product_id",
            graphQLFieldKey: "product",
            graphQLSelection: "id",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "price",
            graphQLFieldKey: "price",
            skipGraphQLSelection: true,
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "tax_lines",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
            graphQLFieldKey: "taxLines",
            graphQLSelection: `
            channelLiable
            priceSet {
            presentmentMoney{
                amount
                currencyCode
              }
              shopMoney {
                amount
                currencyCode
              }
            }
            rate
            ratePercentage
            title`,
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "title",
            graphQLFieldKey: "title",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "gift_card",
            graphQLFieldKey: "isGiftCard",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Custom",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "custom",
            graphQLFieldKey: "custom",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
          },
        },
      },
      {
        name: "Applied Discount",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "applied_discount",
            graphQLFieldKey: "appliedDiscount",
            availableInWebhook: ["draft_orders/create", "draft_orders/update"],
            graphQLSelection: `
          amountSet {
            presentmentMoney {
              amount
              currencyCode
            }
            shopMoney {
              amount
              currencyCode
            }
          }
          description
          title
          value
          valueType
            `,
          },
        },
      },
    ],
  },
};

export const ShopifyDraftOrderLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
